import * as React from "react";
import {
  getYoutubeEmbedUrl,
  getYoutubeVideoId,
} from "@/app/lib/media/youtube";
import { createPlayTimeAccumulator } from "@/app/lib/media/playTimeAccumulator";
import type { PlayTimeProgress } from "@/app/lib/media/playTimeAccumulator";
import { useT } from "@/app/lib/i18n";
import * as s from "./YoutubeLessonPlayer.css";

const POLL_MS = 1000;

type YtPlayer = {
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  playVideo: () => void;
  destroy: () => void;
  getCurrentTime?: () => number;
  getDuration?: () => number;
  getPlayerState?: () => number;
  getPlaybackRate?: () => number;
};

type YtPlayerEvent = { data: number };

type YtNamespace = {
  Player: new (
    elementId: string,
    options: {
      videoId: string;
      width?: string | number;
      height?: string | number;
      playerVars?: Record<string, string | number>;
      events?: {
        onReady?: () => void;
        onStateChange?: (event: YtPlayerEvent) => void;
        onError?: () => void;
      };
    },
  ) => YtPlayer;
  PlayerState: {
    ENDED: number;
    PLAYING: number;
    PAUSED: number;
  };
};

declare global {
  interface Window {
    YT?: YtNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let youtubeApiPromise: Promise<void> | null = null;

function loadYoutubeIframeApi(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("YouTube API requires a browser"));
  }
  if (window.YT?.Player) {
    return Promise.resolve();
  }
  if (youtubeApiPromise) return youtubeApiPromise;

  youtubeApiPromise = new Promise<void>((resolve) => {
    const prior = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prior?.();
      resolve();
    };
    if (window.YT?.Player) {
      resolve();
      return;
    }
    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
    }
  });

  return youtubeApiPromise;
}

export type YoutubeLessonPlayerProps = {
  videoUrl: string;
  initialWatchedSeconds?: number;
  onEnded: () => void;
  onWatchProgress?: (progress: PlayTimeProgress) => void;
  onPersist?: () => void;
  onTrackingUnavailable?: () => void;
};

export function YoutubeLessonPlayer({
  videoUrl,
  initialWatchedSeconds = 0,
  onEnded,
  onWatchProgress,
  onPersist,
  onTrackingUnavailable,
}: YoutubeLessonPlayerProps) {
  const t = useT();
  const reactId = React.useId().replace(/:/g, "");
  const playerElementId = `yt-lesson-${reactId}`;
  const rootRef = React.useRef<HTMLDivElement>(null);
  const playerRef = React.useRef<YtPlayer | null>(null);
  const onEndedRef = React.useRef(onEnded);
  onEndedRef.current = onEnded;
  const onWatchProgressRef = React.useRef(onWatchProgress);
  onWatchProgressRef.current = onWatchProgress;
  const onPersistRef = React.useRef(onPersist);
  onPersistRef.current = onPersist;
  const onTrackingUnavailableRef = React.useRef(onTrackingUnavailable);
  onTrackingUnavailableRef.current = onTrackingUnavailable;
  const accumRef = React.useRef(createPlayTimeAccumulator(POLL_MS / 1000));
  const pollRef = React.useRef<number | null>(null);

  const [apiFailed, setApiFailed] = React.useState(false);
  const videoId = getYoutubeVideoId(videoUrl);

  const stopPoll = React.useCallback(() => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const sample = React.useCallback(() => {
    const player = playerRef.current;
    if (!player?.getCurrentTime || !player.getDuration) return;
    let currentTime = 0;
    let duration = 0;
    let rate = 1;
    try {
      currentTime = player.getCurrentTime();
      duration = player.getDuration();
      rate = player.getPlaybackRate?.() ?? 1;
    } catch {
      return;
    }
    const progress = accumRef.current.tick({
      currentTime,
      duration,
      playbackRate: rate,
    });
    onWatchProgressRef.current?.(progress);
  }, []);

  const startPoll = React.useCallback(() => {
    if (pollRef.current != null) return;
    pollRef.current = window.setInterval(sample, POLL_MS);
  }, [sample]);

  React.useEffect(() => {
    accumRef.current = createPlayTimeAccumulator(POLL_MS / 1000);
    accumRef.current.seed(initialWatchedSeconds);
  }, [videoId]);

  React.useEffect(() => {
    accumRef.current.seed(initialWatchedSeconds);
  }, [initialWatchedSeconds]);

  React.useEffect(() => {
    if (!videoId) return;

    let cancelled = false;
    setApiFailed(false);

    void loadYoutubeIframeApi()
      .then(() => {
        if (cancelled || !window.YT?.Player) {
          if (!cancelled) {
            setApiFailed(true);
            onTrackingUnavailableRef.current?.();
          }
          return;
        }
        const mountEl = document.getElementById(playerElementId);
        if (!mountEl) {
          if (!cancelled) {
            setApiFailed(true);
            onTrackingUnavailableRef.current?.();
          }
          return;
        }

        try {
          playerRef.current?.destroy();
        } catch {
          // ignore
        }

        const rect = rootRef.current?.getBoundingClientRect();
        const width = Math.max(1, Math.round(rect?.width ?? 640));
        const height = Math.max(1, Math.round(rect?.height ?? width * (9 / 16)));
        const playingState = window.YT.PlayerState?.PLAYING;
        const endedState = window.YT.PlayerState?.ENDED;
        const pausedState = window.YT.PlayerState?.PAUSED;

        playerRef.current = new window.YT.Player(playerElementId, {
          videoId,
          width,
          height,
          playerVars: {
            rel: 0,
            modestbranding: 1,
            playsinline: 1,
            enablejsapi: 1,
            origin: window.location.origin,
          },
          events: {
            onStateChange: (event) => {
              if (event.data === playingState) {
                try {
                  const tNow = playerRef.current?.getCurrentTime?.() ?? 0;
                  accumRef.current.markPosition(tNow);
                } catch {
                  // ignore
                }
                startPoll();
              } else if (event.data === pausedState) {
                sample();
                stopPoll();
                onPersistRef.current?.();
              } else if (event.data === endedState) {
                sample();
                stopPoll();
                onPersistRef.current?.();
                onEndedRef.current();
              }
            },
            onError: () => {
              setApiFailed(true);
              onTrackingUnavailableRef.current?.();
            },
          },
        });
      })
      .catch(() => {
        if (!cancelled) {
          setApiFailed(true);
          onTrackingUnavailableRef.current?.();
        }
      });

    return () => {
      cancelled = true;
      stopPoll();
      try {
        playerRef.current?.destroy();
      } catch {
        // ignore destroy races when the node was already replaced
      }
      playerRef.current = null;
    };
  }, [videoId, playerElementId, sample, startPoll, stopPoll]);

  if (!videoId) {
    return null;
  }

  const embedSrc = getYoutubeEmbedUrl(videoId, {
    rel: 0,
    modestbranding: 1,
    playsinline: 1,
    enablejsapi: 1,
    origin: typeof window !== "undefined" ? window.location.origin : undefined,
  });

  return (
    <div ref={rootRef} className={s.root}>
      {apiFailed ? (
        <iframe
          className={s.iframe}
          title={t("certification.lesson.youtubeTrainingVideo")}
          src={embedSrc}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <div id={playerElementId} className={s.mount} />
      )}
    </div>
  );
}
